import React from 'react';
import { Grid } from '@mui/material';
import {
  Refresh,
  Add,
  People,
  Business,
  SwapHoriz,
  Description,
  Campaign,
  History,
  Email,
  SystemUpdateAlt,
} from '@mui/icons-material';
import { useTranslation } from '../../../i18n';
import { SimpleToolCard } from './SimpleToolCard';
import { MetadataExtractionCard } from './MetadataExtractionCard';
import { FileHashBackfillCard } from './FileHashBackfillCard';
import { S3AuditCard } from './S3AuditCard';

interface AdminToolsGridProps {
  isXerAdmin: boolean;
  onOpenAddDrone: () => void;
  onOpenUserManagement: () => void;
  onOpenOrgManagement: () => void;
  onOpenAnnouncements: () => void;
  onOpenDroneAssignments: () => void;
  onOpenMaintenanceDocs: () => void;
  onOpenTenderEmails: () => void;
  onOpenLegacyHours: () => void;
  onOpenMaintenanceRecalculate: () => void;
  onOpenSoftwareReleases: () => void;
}

/**
 * The grid of admin tool cards. Launch-only cards are rendered via SimpleToolCard;
 * the three stateful cards (metadata extraction, file-hash backfill, S3 audit) own
 * their own data layer. All open callbacks are threaded from AdminToolsPage.
 */
export const AdminToolsGrid: React.FC<AdminToolsGridProps> = ({
  isXerAdmin,
  onOpenAddDrone,
  onOpenUserManagement,
  onOpenOrgManagement,
  onOpenAnnouncements,
  onOpenDroneAssignments,
  onOpenMaintenanceDocs,
  onOpenTenderEmails,
  onOpenLegacyHours,
  onOpenMaintenanceRecalculate,
  onOpenSoftwareReleases,
}) => {
  const { t } = useTranslation();

  return (
    <Grid container spacing={3} data-testid="admin-tools-grid">
      {/* Metadata Re-extraction Tool - XER Admin Only */}
      {isXerAdmin && (
        <Grid item xs={12} md={6} lg={4}>
          <MetadataExtractionCard isXerAdmin={isXerAdmin} />
        </Grid>
      )}

      {/* Add Drone Tool - XER Admin Only */}
      {isXerAdmin && (
        <Grid item xs={12} md={6} lg={4}>
          <SimpleToolCard
            icon={<Add />}
            title={t('adminToolsPage.addDrone.title')}
            description={t('adminToolsPage.addDrone.description')}
            chipLabel={t('adminToolsPage.xerAdminOnly')}
            buttonLabel={t('adminToolsPage.addDrone.buttonLabel')}
            onLaunch={onOpenAddDrone}
            testId="admin-tools-launch-add-drone"
          />
        </Grid>
      )}

      {/* User Management Tool - Available to All Admins */}
      <Grid item xs={12} md={6} lg={4}>
        <SimpleToolCard
          icon={<People />}
          title={t('adminToolsPage.userManagement.title')}
          description={t('adminToolsPage.userManagement.description')}
          buttonLabel={t('adminToolsPage.userManagement.viewUsers')}
          onLaunch={onOpenUserManagement}
          testId="admin-tools-launch-user-management"
        />
      </Grid>

      {/* Organization Management Tool - XER Admin Only */}
      {isXerAdmin && (
        <Grid item xs={12} md={6} lg={4}>
          <SimpleToolCard
            icon={<Business />}
            title={t('adminToolsPage.orgManagement.title')}
            description={t('adminToolsPage.orgManagement.description')}
            chipLabel={t('adminToolsPage.xerAdminOnly')}
            buttonLabel={t('adminToolsPage.orgManagement.viewOrganizations')}
            onLaunch={onOpenOrgManagement}
            testId="admin-tools-launch-org-management"
          />
        </Grid>
      )}

      {/* Announcements Tool - XER Admin Only */}
      {isXerAdmin && (
        <Grid item xs={12} md={6} lg={4}>
          <SimpleToolCard
            icon={<Campaign />}
            title={t('adminToolsPage.announcements.title')}
            description={t('adminToolsPage.announcements.description')}
            chipLabel={t('adminToolsPage.xerAdminOnly')}
            buttonLabel={t('adminToolsPage.announcements.manageAnnouncements')}
            onLaunch={onOpenAnnouncements}
            testId="admin-tools-launch-announcements"
          />
        </Grid>
      )}

      {/* Drone Assignments Tool - XER Admin Only */}
      {isXerAdmin && (
        <Grid item xs={12} md={6} lg={4}>
          <SimpleToolCard
            icon={<SwapHoriz />}
            title={t('adminToolsPage.droneAssignments.title')}
            description={t('adminToolsPage.droneAssignments.description')}
            chipLabel={t('adminToolsPage.xerAdminOnly')}
            buttonLabel={t('adminToolsPage.droneAssignments.manageAssignments')}
            onLaunch={onOpenDroneAssignments}
            testId="admin-tools-launch-drone-assignments"
          />
        </Grid>
      )}

      {/* Maintenance Documents Tool - XER Admin Only */}
      {isXerAdmin && (
        <Grid item xs={12} md={6} lg={4}>
          <SimpleToolCard
            icon={<Description />}
            title={t('adminToolsPage.maintenanceDocs.title')}
            description={t('adminToolsPage.maintenanceDocs.description')}
            chipLabel={t('adminToolsPage.xerAdminOnly')}
            buttonLabel={t('adminToolsPage.maintenanceDocs.manageDocuments')}
            onLaunch={onOpenMaintenanceDocs}
            testId="admin-tools-launch-maintenance-docs"
          />
        </Grid>
      )}

      {/* Tender Emails Tool - XER Admin Only */}
      {isXerAdmin && (
        <Grid item xs={12} md={6} lg={4}>
          <SimpleToolCard
            icon={<Email />}
            title={t('adminToolsPage.tenderEmails.title')}
            description={t('adminToolsPage.tenderEmails.description')}
            chipLabel={t('adminToolsPage.xerAdminOnly')}
            buttonLabel={t('adminToolsPage.tenderEmails.viewEmails')}
            onLaunch={onOpenTenderEmails}
            testId="admin-tools-launch-tender-emails"
          />
        </Grid>
      )}

      {/* File Hash Backfill Tool - XER Admin Only */}
      {isXerAdmin && (
        <Grid item xs={12} md={6} lg={4}>
          <FileHashBackfillCard />
        </Grid>
      )}

      {/* S3 Consistency Audit Tool - XER Admin Only */}
      {isXerAdmin && (
        <Grid item xs={12} md={6} lg={4}>
          <S3AuditCard />
        </Grid>
      )}

      {/* Legacy Hours Migration Tool - XER Admin Only */}
      {isXerAdmin && (
        <Grid item xs={12} md={6} lg={4}>
          <SimpleToolCard
            icon={<History />}
            title="Legacy Hours Migration"
            description="Enter historical flight/engine hours from pre-2023, 2023, and 2024 for drones and pilots. These hours are for records only and do not affect maintenance scheduling."
            chipLabel="XER Admin Only"
            buttonLabel="Manage Legacy Hours"
            onLaunch={onOpenLegacyHours}
            testId="admin-tools-launch-legacy-hours"
          />
        </Grid>
      )}

      {/* Maintenance Recalculate Tool - XER Admin Only */}
      {isXerAdmin && (
        <Grid item xs={12} md={6} lg={4}>
          <SimpleToolCard
            icon={<Refresh />}
            title={t('adminToolsPage.maintenanceRecalculate.title')}
            description={t('adminToolsPage.maintenanceRecalculate.description')}
            chipLabel={t('adminToolsPage.xerAdminOnly')}
            buttonLabel={t('adminToolsPage.maintenanceRecalculate.buttonLabel')}
            onLaunch={onOpenMaintenanceRecalculate}
            testId="admin-tools-launch-maintenance-recalculate"
          />
        </Grid>
      )}

      {/* Software Releases Tool - XER Admin Only */}
      {isXerAdmin && (
        <Grid item xs={12} md={6} lg={4}>
          <SimpleToolCard
            icon={<SystemUpdateAlt />}
            title="Software Releases"
            description="Publish and manage the installer releases offered on the Downloads page (XER Mission Planner, XER UC Flasher, XER H16 QGC)."
            chipLabel="XER Admin Only"
            buttonLabel="Manage Releases"
            onLaunch={onOpenSoftwareReleases}
            testId="admin-tools-launch-software-releases"
          />
        </Grid>
      )}
    </Grid>
  );
};
